import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AccountInScopeDto, ChildAccountsResponse } from '@xcentral/api-client';
import { useApi } from '../hooks/useApi';
import { useClientTiers } from './ClientTiersProvider';
import { AccountContext, ACCOUNT_QUERY_PARAM, type AccountContextValue } from './useAccountContext';

/**
 * BT001 #16306 — resolves the active account for THIS TAB from the URL.
 *
 * Mounts inside ClientTiersProvider (it consumes that context for the caller's
 * own accountId, so no second `/client-it/me` round trip) and wraps ClientLayout.
 *
 * Resolution (see the Story's flow diagram):
 *   ?account present AND in the server-returned scope -> use it
 *   otherwise                                         -> caller's own account,
 *                                                        then canonicalise the URL
 *                                                        via replace (BR-ACTX-005, AC-6)
 *
 * A 403 from /child-accounts (ClientBilling / ClientUser) is the NORMAL state for
 * two of the three client tiers, not an error: scope is empty, isParent is false,
 * and the caller's own account stays active (AC-8).
 */
export function AccountContextProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId: ownAccountId, loading: tiersLoading } = useClientTiers();

  const [accountsInScope, setAccountsInScope] = useState<AccountInScopeDto[]>([]);
  const [scopeResolved, setScopeResolved] = useState(false);

  // The URL is read every render — never copied into state. That is what makes two
  // tabs independent (AC-4): each MemoryRouter/browser tab has its own location, and
  // there is no shared store for one to write and the other to observe.
  const paramAccountId = useMemo(
    () => new URLSearchParams(location.search).get(ACCOUNT_QUERY_PARAM),
    [location.search],
  );

  useEffect(() => {
    if (tiersLoading) return;
    if (!ownAccountId) {
      // No PortalUser profile resolved — nothing to scope. Settle so consumers can
      // surface their own no-profile state rather than spinning forever.
      setAccountsInScope([]);
      setScopeResolved(true);
      return;
    }

    let mounted = true;
    const ctrl = new AbortController();
    setScopeResolved(false);

    (async () => {
      try {
        const resp = await api.get<ChildAccountsResponse>(
          '/api/v1/client-it/child-accounts',
          ctrl.signal,
        );
        if (!mounted) return;
        // The generated ChildAccountsResponse.items is nullable — defend it.
        setAccountsInScope(resp.items ?? []);
      } catch (err) {
        if (!mounted || (err as DOMException)?.name === 'AbortError') return;
        // 403 = not a ClientAdmin. Normal for ClientBilling/ClientUser (AC-8), and
        // any other failure degrades the same way: no switcher, own account intact.
        setAccountsInScope([]);
      } finally {
        if (mounted) setScopeResolved(true);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [api, ownAccountId, tiersLoading]);

  const loading = tiersLoading || !scopeResolved;

  // An id the server never offered — malformed, another tenant's, a grandchild's —
  // drops back to the caller's own account BEFORE any request is issued (AC-5).
  const activeAccountId = useMemo(() => {
    if (loading) return null;
    if (paramAccountId && accountsInScope.some((a) => a.id === paramAccountId)) return paramAccountId;
    return ownAccountId;
  }, [loading, paramAccountId, accountsInScope, ownAccountId]);

  const buildAccountUrl = useCallback(
    (accountId: string) => {
      // Same pathname, every other query param preserved, only `account` replaced (AC-3).
      const params = new URLSearchParams(location.search);
      params.set(ACCOUNT_QUERY_PARAM, accountId);
      return `${location.pathname}?${params.toString()}${location.hash}`;
    },
    [location.pathname, location.search, location.hash],
  );

  // Canonicalise: the URL always carries the resolved account (BR-ACTX-005, AC-6).
  // `replace` so entering the portal without ?account adds no junk history entry.
  useEffect(() => {
    if (loading || !activeAccountId) return;
    if (paramAccountId === activeAccountId) return;
    navigate(buildAccountUrl(activeAccountId), { replace: true });
  }, [loading, activeAccountId, paramAccountId, navigate, buildAccountUrl]);

  // The control NAVIGATES; it does not mutate a store. No localStorage, no
  // sessionStorage, no server-side "current account" (BR-ACTX-003).
  const switchTo = useCallback(
    (accountId: string) => {
      navigate(buildAccountUrl(accountId));
    },
    [navigate, buildAccountUrl],
  );

  const ownAccount = useMemo(
    () => accountsInScope.find((a) => a.id === ownAccountId),
    [accountsInScope, ownAccountId],
  );

  // IsParent is true on the caller's OWN row iff it has at least one direct child.
  // Nothing to switch between means no control at all (BR-ACTX-002, AC-2).
  const isParent = ownAccount?.isParent === true && accountsInScope.length > 1;

  const value = useMemo<AccountContextValue>(() => {
    const active = accountsInScope.find((a) => a.id === activeAccountId);
    return {
      activeAccountId,
      activeAccountName: active?.name ?? '',
      ownAccountId,
      ownAccountName: ownAccount?.name ?? '',
      accountsInScope,
      isParent,
      loading,
      switchTo,
    };
  }, [activeAccountId, accountsInScope, ownAccountId, ownAccount, isParent, loading, switchTo]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}
